import type { BackupDocument } from './types';

interface EncryptedBackup {
  format: 'edgesub-backup';
  version: 1;
  salt: string;
  iv: string;
  ciphertext: string;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function encode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function decode(value: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(value), (char) => char.charCodeAt(0));
}

async function deriveKey(password: string, salt: Uint8Array<ArrayBuffer>) {
  const material = await crypto.subtle.importKey('raw', encoder.encode(password), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: 250_000 },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptBackup(document: BackupDocument, password: string): Promise<Blob> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(password, salt);
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv }, key, encoder.encode(JSON.stringify(document)),
  ));
  const payload: EncryptedBackup = {
    format: 'edgesub-backup', version: 1, salt: encode(salt), iv: encode(iv), ciphertext: encode(ciphertext),
  };
  return new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
}

export async function decryptBackup(file: File, password: string): Promise<BackupDocument> {
  const payload = JSON.parse(await file.text()) as EncryptedBackup;
  if (payload.format !== 'edgesub-backup' || payload.version !== 1) throw new Error('无法识别的 EdgeSub 备份文件');
  const key = await deriveKey(password, decode(payload.salt));
  try {
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: decode(payload.iv) }, key, decode(payload.ciphertext));
    return JSON.parse(decoder.decode(plaintext)) as BackupDocument;
  } catch {
    throw new Error('备份密码不正确或文件已损坏');
  }
}
